import { Banner, BlockStack, Button, Text } from '@shopify/polaris';
import { useEffect, useRef, useState } from 'react';
import type { StoreInventoryProgress } from '../../../../packages/shared/shopify-privacy/store-review';
import { useApi } from '../../hooks/useApi';
import StoreRemovalApproval from './StoreRemovalApproval';

type Response = { review: StoreInventoryProgress | null };
export default function StoreInventory({ eventId, revision, onApplied }: { eventId: string; revision: number; onApplied:()=>Promise<void> }) {
  const { fetchApi, loading, error } = useApi();
  const [review, setReview] = useState<StoreInventoryProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState('');
  const run = useRef(0);
  const path = `/api/shopify-privacy/${encodeURIComponent(eventId)}/store-inventory`;
  useEffect(() => {
    const current = ++run.current;
    setReview(null); setRunning(false); setNotice('');
    void fetchApi<Response>(path).then(result => { if (current === run.current && result) setReview(result.review); });
    return () => { run.current++; };
  }, [fetchApi, path, revision]);

  const prepare = async () => {
    const current = ++run.current;
    setRunning(true); setNotice('');
    let input: Record<string, unknown> = { revision };
    while (current === run.current) {
      const result = await fetchApi<Response>(path, { method: 'POST', body: JSON.stringify(input) });
      if (current !== run.current) return;
      if (!result?.review) break;
      const next = result.review;
      setReview(next);
      if (next.phase !== 'records' && next.phase !== 'files') break;
      if (input.review_revision === next.revision) {
        setNotice('The saved position did not change. Refresh the review before continuing.'); break;
      }
      input = { review_id: next.id, review_revision: next.revision };
    }
    if (current === run.current) setRunning(false);
  };
  const listed = review && review.phase !== 'records' && review.phase !== 'files';
  return <BlockStack gap="200">
    <Text as="h3" variant="headingSm">Store records and private files</Text>
    <Text as="p">Prepare a list of records and files linked to this disconnected store. You can close the page and resume later.</Text>
    {review && <Text as="p">{review.reviewed_sections} of {review.sections} record groups checked. Found {review.records.toLocaleString()} {review.records === 1 ? 'record' : 'records'} and {review.private_files.toLocaleString()} private {review.private_files === 1 ? 'file' : 'files'}. {review.reviewed_directories} of {review.directories} folders checked.</Text>}
    {review?.older_requests && <Text as="p">
      {review.older_requests.completed > 0 && `${review.older_requests.completed} earlier ${review.older_requests.completed === 1 ? 'receipt matched' : 'receipts matched'} a completed request. `}
      {review.older_requests.covered > 0 && `This store request covers ${review.older_requests.covered} earlier ${review.older_requests.covered === 1 ? 'receipt' : 'receipts'}. `}
      {review.older_requests.unresolved > 0 && `A platform administrator still needs to verify ${review.older_requests.unresolved} earlier ${review.older_requests.unresolved === 1 ? 'receipt' : 'receipts'}.`}
    </Text>}
    {error && <Banner tone="critical">{error}</Banner>}
    {notice && <Text as="p">{notice}</Text>}
    {listed ? review.blockers?.length
      ? <Banner tone="warning" title="Before this store can be removed"><ul>{review.blockers.map(reason => <li key={reason}>{reason}</li>)}</ul></Banner>
      : <StoreRemovalApproval key={review.id} eventId={eventId} review={review} onReview={setReview} onApplied={onApplied} />
      : running ? <Button onClick={() => { run.current++; setRunning(false); setNotice('Paused. The current page can finish saving.'); }}>Pause preparation</Button>
        : <Button disabled={loading} onClick={() => void prepare()}>{review ? 'Continue preparing list' : 'Prepare record and file list'}</Button>}
  </BlockStack>;
}
